import * as React from 'react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import hasFeature from 'modules/core/lib/hasFeature'

import { IncidenceRules } from 'modules/bookkeepers/store/collections/incidenceRules'
import { Incidences } from 'modules/bookkeepers/store/collections/incidences'
import type Incidence from 'modules/bookkeepers/store/models/Incidence'
import { INCIDENCE_STATUSES } from 'modules/payroll_bundle/lib/Incidences/Status'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'

const useEmployeeIncidenceRules = (employeeId: string | number) => {
  const [hiringIncidence, setHiringIncidence] = React.useState<Incidence>()

  const professionalsActivated = hasFeature('factorial_professionals')
  useAsyncData(async () => {
    if (!professionalsActivated) {
      return null
    }
    const incidences = new Incidences()
    await incidences.fetch({
      data: {
        employee_ids: [employeeId],
        type: [INCIDENCE_TYPES.hiring],
        status: [
          INCIDENCE_STATUSES.in_preparation,
          INCIDENCE_STATUSES.to_do,
          INCIDENCE_STATUSES.doing,
        ],
      },
    })
    setHiringIncidence(incidences.last())
  }, [employeeId, professionalsActivated])

  const useFetchIncidenceRules = (incidence?: Incidence) => {
    const incidenceRules = useLazyRef(() => new IncidenceRules()).current
    const incidenceType = incidence?.attributes?.name
    const companyId = incidence?.attributes?.company_id

    const { loading } = useAsyncData(() => {
      if (incidenceType && companyId) {
        incidenceRules.fetch({
          data: {
            incidence_type: incidenceType,
            company_id: companyId,
          },
        })
      }
      return Promise.resolve()
    }, [incidenceType, companyId])

    return {
      loading,
      incidenceRules,
    }
  }

  const { incidenceRules } = useFetchIncidenceRules(hiringIncidence)

  return {
    professionalsActivated,
    hiringIncidence,
    incidenceRules,
  }
}

export default useEmployeeIncidenceRules
